import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { TrendingUp, Shield, Calculator, ArrowRight } from 'lucide-react';
import api from '../lib/api';

interface CoinProtocol {
  name: string;
  slug: string;
  chain: string;
  category: string;
  apy: string;
}

const COIN_INFO: Record<string, { ticker: string; description: string }> = {
  ethereum: { ticker: 'ETH', description: 'Ethereum staking lets you earn passive income by helping secure the world\'s largest smart contract platform. Stake via native validators, liquid staking protocols like Lido and Rocket Pool, or centralized exchanges.' },
  solana: { ticker: 'SOL', description: 'Solana staking offers competitive yields through native delegation to validators. With fast transactions and low fees, Solana is one of the most popular staking networks.' },
  cosmos: { ticker: 'ATOM', description: 'Cosmos Hub (ATOM) staking offers some of the highest yields in the ecosystem. Delegate to validators through Keplr wallet and earn rewards while helping secure the interchain.' },
  polkadot: { ticker: 'DOT', description: 'Polkadot staking through nomination pools makes it accessible to all holders. Earn rewards by nominating validators who secure the relay chain.' },
  cardano: { ticker: 'ADA', description: 'Cardano staking is unique — your ADA is never locked and you can spend it at any time while still earning rewards through stake pool delegation.' },
  avalanche: { ticker: 'AVAX', description: 'Avalanche staking secures one of the fastest smart contract platforms. Delegate AVAX to validators with a minimum of 25 AVAX.' },
  polygon: { ticker: 'MATIC', description: 'Polygon staking helps secure the popular Ethereum scaling solution. Delegate to validators and earn MATIC rewards.' },
  bnb: { ticker: 'BNB', description: 'BNB Chain staking lets you earn passive income on one of the most widely used blockchains in crypto.' },
  near: { ticker: 'NEAR', description: 'NEAR Protocol staking offers competitive yields through validator delegation on this developer-friendly blockchain.' },
  fantom: { ticker: 'FTM', description: 'Fantom staking rewards validators and delegators for securing this high-speed, low-cost DeFi network.' },
  tezos: { ticker: 'XTZ', description: 'Tezos baking (staking) is built into the protocol. Delegate to bakers and earn XTZ rewards with no lock-up period.' },
  algorand: { ticker: 'ALGO', description: 'Algorand governance staking lets you earn rewards while participating in protocol governance decisions.' },
  bitcoin: { ticker: 'BTC', description: 'Bitcoin staking is available through wrapped BTC protocols and liquid staking platforms that bring staking yields to the world\'s largest cryptocurrency.' },
};

export default function CoinStakingPage() {
  const { coin } = useParams<{ coin: string }>();
  const [protocols, setProtocols] = useState<CoinProtocol[]>([]);
  const [loading, setLoading] = useState(true);

  const coinName = coin ? coin.charAt(0).toUpperCase() + coin.slice(1) : '';
  const info = COIN_INFO[coin || ''] || { ticker: coinName.toUpperCase(), description: `Compare ${coinName} staking rates across multiple protocols.` };

  useEffect(() => {
    if (!coin) return;
    api.get(`/staking/${coin}`)
      .then(res => {
        setProtocols(res.data.data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [coin]);

  const topApy = protocols.length > 0 ? protocols[0].apy : '0';

  return (
    <div className="min-h-screen bg-navy-dark py-12">
      <SEO
        title={`${coinName} (${info.ticker}) Staking — Best APY Rates 2026 | ResidualVault`}
        description={`Compare ${coinName} staking rates across ${protocols.length} protocols. Top APY: ${topApy}%. Live rates, risk analysis, and staking calculator.`}
        canonical={`https://www.residualvault.com/staking/${coin}`}
        keywords={`${coinName} staking, ${info.ticker} staking APY, ${coinName} staking rewards, best ${coinName} staking rates`}
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4">
            <span className="text-cream">{coinName} </span>
            <span className="gradient-text-neon">Staking</span>
          </h1>
          <p className="text-xl text-sky-blue max-w-3xl mx-auto">
            Compare the best {coinName} ({info.ticker}) staking rates across {protocols.length} protocols — updated in real time
          </p>
        </div>

        <div className="card-premium mb-8">
          <h2 className="text-2xl font-bold text-cream mb-3">About {coinName} Staking</h2>
          <p className="text-sky-blue text-lg">{info.description}</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-neon-red mx-auto mb-4" />
            <p className="text-sky-blue">Loading {coinName} protocols...</p>
          </div>
        ) : protocols.length > 0 ? (
          <div className="card-premium mb-8">
            <h2 className="text-2xl font-bold text-cream mb-6">
              {coinName} Staking Protocols — Ranked by APY
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-sky-blue/20">
                    <th className="text-left py-3 px-4 text-sky-blue text-sm uppercase tracking-wider">#</th>
                    <th className="text-left py-3 px-4 text-sky-blue text-sm uppercase tracking-wider">Protocol</th>
                    <th className="text-left py-3 px-4 text-sky-blue text-sm uppercase tracking-wider">Category</th>
                    <th className="text-right py-3 px-4 text-sky-blue text-sm uppercase tracking-wider">APY</th>
                    <th className="text-right py-3 px-4 text-sky-blue text-sm uppercase tracking-wider"></th>
                  </tr>
                </thead>
                <tbody>
                  {protocols.map((p, i) => (
                    <tr key={p.slug} className="border-b border-sky-blue/10 hover:bg-steel-blue/10 transition-colors">
                      <td className="py-3 px-4 text-sky-blue">{i + 1}</td>
                      <td className="py-3 px-4">
                        <Link to={`/protocol/${p.slug}`} className="text-cream font-medium hover:text-neon-red transition-colors">
                          {p.name}
                        </Link>
                      </td>
                      <td className="py-3 px-4 text-sky-blue text-sm">{p.category}</td>
                      <td className="py-3 px-4 text-right">
                        <span className="gradient-text-neon font-bold text-lg">{parseFloat(p.apy).toFixed(2)}%</span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Link to={`/protocol/${p.slug}`} className="text-neon-red hover:underline text-sm flex items-center justify-end gap-1">
                          Details <ArrowRight className="w-3 h-3" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="card-premium text-center py-8 mb-8">
            <p className="text-sky-blue">No {coinName} staking protocols found. <Link to="/staking" className="text-neon-red hover:underline">Browse all protocols</Link></p>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Link to="/calculator" className="card-premium text-center hover:border-neon-red/30 transition-all">
            <Calculator className="w-8 h-8 text-neon-red mx-auto mb-3" />
            <h3 className="text-lg font-bold text-cream mb-1">Calculate Returns</h3>
            <p className="text-sky-blue text-sm">See how much you'd earn staking {info.ticker}</p>
          </Link>
          <Link to="/leaderboard" className="card-premium text-center hover:border-neon-red/30 transition-all">
            <TrendingUp className="w-8 h-8 text-neon-red mx-auto mb-3" />
            <h3 className="text-lg font-bold text-cream mb-1">APY Leaderboard</h3>
            <p className="text-sky-blue text-sm">See top yields across all chains</p>
          </Link>
          <Link to="/staking" className="card-premium text-center hover:border-neon-red/30 transition-all">
            <Shield className="w-8 h-8 text-neon-red mx-auto mb-3" />
            <h3 className="text-lg font-bold text-cream mb-1">All 156+ Protocols</h3>
            <p className="text-sky-blue text-sm">Compare every tracked protocol</p>
          </Link>
        </div>

        <div className="card-premium text-center">
          <h2 className="text-3xl font-bold mb-4">
            <span className="text-cream">Start Earning with </span>
            <span className="gradient-text-neon">{coinName}</span>
          </h2>
          <p className="text-sky-blue mb-6">Create a free account to track {coinName} staking rates and get APY change alerts</p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/register" className="btn-neon inline-flex items-center">Create Free Account</Link>
            <Link to="/staking" className="bg-steel-blue/20 border border-sky-blue text-sky-blue font-semibold px-6 py-3 rounded-lg hover:bg-steel-blue/30 transition-all inline-flex items-center">Browse All Protocols</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
